import { TenantProperty } from '@models/Tenant/tenant.model';
import { Router } from '@angular/router';
import { EmailApi } from '@models/RutersApi';
import { UserSearch } from '@models/User/UserSearch';
import { UserProfile } from '@models/User/UserProfile';
import { PrivacyStatus } from '@models/User/PrivacyStatus';
import { UserCertificateDto } from '@models/SignType/UserCertificateDto';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import { switchMap, shareReplay, tap, map } from 'rxjs/operators';
import { WebService } from '@services/web.service';
import { UserData } from '@models/User/UserData';
import { UserDataApi } from '@models/RutersApi';
import { routerLink } from '@models/constants';
import { AppLink } from '@models/app-link-model';
import { AppLinkLocation } from '@models/app-link-model';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from '@services/user/toastr.service';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {

  constructor(private webservice: WebService,
    private router: Router,
    private http: HttpClient,
    private toastrService: ToastrService,
  ) {}

  private reloadTrigger$ = new BehaviorSubject<void>(undefined);

  // Definiamo il flusso una volta sola
  public userdata$: Observable<UserData> = this.reloadTrigger$.pipe(
    switchMap(() => this.webservice.httpGet<UserData>(UserDataApi.RETRIEVE_USER_DATA)),
    shareReplay(1) // Mantiene l'ultimo valore per i nuovi iscritti
  );

  getUserdata(reload: boolean = false): Observable<UserData> {
    if (reload) {
      // Questo "spinge" un nuovo valore nel trigger,
      // forzando lo switchMap a rifare la chiamata HTTP
      this.reloadTrigger$.next();
    }
    return this.userdata$;
  }

  /*
  public userdata$: Observable<any> | null = null; // Cache della chiamata
  getUserdata(reload: boolean = false): Observable<UserData> {
    if (!this.userdata$ || reload) {
      this.userdata$ = this.webservice.httpGet(UserDataApi.RETRIEVE_USER_DATA).pipe(
        shareReplay(1)
      );
    }
    return this.userdata$;
  }
    */

  updateLanguage(langEnum: string) {
    return this.webservice.httpPut(UserDataApi.USER_UPDATE_LANG, { language: langEnum });
  }

  updateProfile(userProfile: UserProfile) {
    return this.webservice.httpPut(
      UserDataApi.USER_UPDATE_PROFILE,
      userProfile
    );
  }

  updateSettings(userProfile: UserProfile) {
    return this.webservice.httpPut(
      UserDataApi.USER_UPDATE_SETTINGS,
      userProfile
    );
  }

  saveSettings(setting:any) {
    return this.webservice.httpPost(UserDataApi.USER_SETTINGS, setting);
  }

  privacyAccept(): Observable<PrivacyStatus> {
    return this.webservice.httpPut<PrivacyStatus>(UserDataApi.USER_ACCEPT_PRIVACY, {}).pipe(
      tap((privacyStatus) => {
        this.getUserdata(true).subscribe((userdata: UserData) => {});
      }),
    );
  }

  getUserLinks(location: string): Observable<AppLink[]> {
    return this.getUserdata().pipe(
      map((userdata: UserData) => {
        return [
          // router link
          ...routerLink.filter((link) => link.location === location && userdata.views.indexOf(link.viewName) >= 0),
          // additional link from tenant configuration
          ...(userdata.uiSettings?.menuUrls?.map((menuUrl: any) => {
            return {
              viewName: menuUrl.labelI18n['italiano'],
              title: String(menuUrl.labelI18n['italiano']),
              location: AppLinkLocation.SIDEBAR,
              goToUrl: String(menuUrl.url),
            };
          }) || []),
        ];
      }),
    );
  }

  retrieveUsers({ name, surname, email, onlyActive }: UserSearch): Observable<UserData[]> {
    return this.webservice.httpGet<UserData[]>(
      UserDataApi.USERS.replace('{email}', email ? email : '')
        .replace('{surname}', surname ? surname : '')
        .replace('{name}', name ? name : '')
        .replace('{onlyActive}', onlyActive.toString()),
    );
  }

  getCurrentTenantUsers({ name, surname, email, onlyActive }: UserSearch): Observable<UserData[]> {
    return this.webservice.httpGet<UserData[]>(UserDataApi.TENANT_USERS
      .replace('{email}', email ? email : '')
      .replace('{surname}', surname ? surname : '')
      .replace('{name}', name ? name : '')
      .replace('{onlyActive}', onlyActive.toString()));
  }

  getAllPersonTenants(): Observable<any> {
    return this.webservice.httpGet<any>(UserDataApi.ALL_PERSONS_TENANTS);
  }

  signerName(signer: UserData) {
    if (signer.name && signer.surname) {
      return signer.name + ' ' + signer.surname;
    } else if (signer.denomination) {
      return signer.denomination;
    } else {
      return signer.mail;
    }
  }

  /**
   * isQualifiedSignature: currentSigner.signType === SignTypeEnum.firmaQualificata
   */
  getCertificates(isQualifiedSignature: boolean): Observable<UserCertificateDto[] | null> {
    return this.getUserdata().pipe(
      map((userdata: UserData) => {
        if (!userdata?.certificates) {
          return null;
        }
        const value = userdata?.certificates.filter(
          ( cert: UserCertificateDto ) => cert.caQualified === isQualifiedSignature)
          return value;
      }
    ));
  }

  isEmailValid(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  sendInviteEmail(userMail: string){
    const body = {userMail: userMail}
    return this.webservice.httpPost(EmailApi.SEND_INVITE_EMAIL, body);
  }

  propertiesLoad(propName: string): Observable<TenantProperty> {
    return this.webservice.httpGet<TenantProperty>(
      UserDataApi.PROPERTIES_LOAD.replace('{propName}', propName)
    );
  }

  // property a livello di tenant: cache condivisa per evitare una chiamata HTTP per ogni firmatario
  private feqOneShotEnabled$: Observable<TenantProperty> | null = null;

  getFeqOneShotEnabled(): Observable<TenantProperty> {
    if (!this.feqOneShotEnabled$) {
      this.feqOneShotEnabled$ = this.propertiesLoad('feq_oneshot_enabled').pipe(shareReplay(1));
    }
    return this.feqOneShotEnabled$;
  }

  propertiesUpdate(propName: string, propValue: string): Observable<TenantProperty> {
    const p: TenantProperty = {propName, propValue} as TenantProperty;
    return this.webservice.httpPut<TenantProperty>(UserDataApi.PROPERTIES_UPDATE, p);
  }

  impersonate(userTarget: string, role: string) {
    const formData = new FormData();
    formData.append('username', userTarget);
    formData.append('role', role);
    return this.http.post(UserDataApi.SWITCH_USER, formData, {
      responseType: 'text',
    });
  }

  switchOff() {
    return this.http.get(UserDataApi.EXIT_SWITCH, { responseType: 'text' });
  }

  hasLegaLRegistryOrSkip(userData: UserData): boolean {
    return (userData.legalRegistry != null || environment['envType'].startsWith('onprem'));
  }

}
